/**
 * PATH: packages/email-hub/src/feature/reader-header/actions/registry.ts
 * FILE_ID: EMAILHUB-READERHEADER-ACTIONS-REGISTRY-001
 * PURPOSE: Keep every pre-body action in one deterministic ownership registry.
 * ROLE IN ARCHITECTURE: Pure domain inventory used by UI, tests, menus, and telemetry.
 * MAIN EXPORTS: READER_ACTIONS, getReaderAction, CORE_COMMAND_ORDER.
 * NON-RESPONSIBILITIES: This file does not execute actions or decide permissions.
 * NOTES FOR FUTURE AI: Add actions here before rendering them; duplicate ids or stable orders are regressions.
 *   Core command order is a product contract (plan D269-14) — never usage-sort it.
 */
import type {
  ReaderActionDescriptor,
  ReaderActionId,
  ReaderErrorMapping,
  ReaderHeaderZone,
} from '../model/types'
import { ACTION_TIMING_MS } from '../tokens'

const POINTER = ['pointer', 'keyboard', 'touch'] as const
const CONTEXTUAL = ['pointer', 'context-menu', 'keyboard', 'touch'] as const

// Single timing source of truth: tokens.ts (plan feedback contract).
const UNDO_TOAST_MS = ACTION_TIMING_MS.undoWindow

/** Default classified copy; actions override only classes with better guidance. */
function errorMapping(
  overrides: Partial<ReaderErrorMapping> = {},
): ReaderErrorMapping {
  return {
    perm: 'You do not have permission for this action.',
    validation: 'The request was rejected as invalid. Nothing changed.',
    notfound: 'The email or its target no longer exists. Refresh to continue.',
    conflict: 'Changed elsewhere. Refresh before retrying.',
    network: 'Network problem. Your change was not saved; retry when connected.',
    server: 'Server error. Nothing changed; try again.',
    ...overrides,
  }
}

type ActionOverrides = Partial<
  Omit<ReaderActionDescriptor, 'id' | 'label' | 'zone' | 'stableOrder' | 'telemetryEvent'>
>

function action(
  id: ReaderActionId,
  label: string,
  zone: ReaderHeaderZone,
  stableOrder: number,
  contract: Pick<
    ReaderActionDescriptor,
    'primaryAction' | 'permission' | 'busyKey' | 'stories'
  >,
  options: ActionOverrides = {},
): ReaderActionDescriptor {
  return {
    id,
    label,
    zone,
    stableOrder,
    inputs: options.inputs ?? POINTER,
    visibility: options.visibility ?? 'conditional',
    overflowGroup: options.overflowGroup,
    visibleWhen: options.visibleWhen ?? (() => true),
    contextActions: options.contextActions ?? [],
    optimistic: options.optimistic ?? false,
    optimisticReducer: options.optimisticReducer ?? 'none',
    undoable: options.undoable ?? false,
    inverse:
      options.inverse ??
      (options.undoable
        ? {
            kind: 'undo-toast',
            undoWindowMs: UNDO_TOAST_MS,
            description: 'Undo restores the prior email state.',
          }
        : { kind: 'none', description: 'Not reversible from the reader.' }),
    dangerous: options.dangerous ?? false,
    errors: options.errors ?? errorMapping(),
    telemetryEvent: `reader_header.${id}`,
    ...contract,
  }
}

export const READER_ACTIONS: readonly ReaderActionDescriptor[] = [
  action('back', 'Back', 'orientation', 10, {
    primaryAction: 'Return to the list preserving scroll, filters, and selection.',
    permission: 'none',
    busyKey: 'orientation:back',
    stories: ['US-02'],
  }, { visibility: 'always', optimisticReducer: 'local-ui-only' }),
  action('previous', 'Previous email', 'orientation', 20, {
    primaryAction: 'Select the previous list item; header paints from cache.',
    permission: 'none',
    busyKey: 'orientation:navigate',
    stories: ['US-01'],
  }, { optimisticReducer: 'local-ui-only' }),
  action('next', 'Next email', 'orientation', 30, {
    primaryAction: 'Select the next list item; header paints from cache.',
    permission: 'none',
    busyKey: 'orientation:navigate',
    stories: ['US-01'],
  }, { optimisticReducer: 'local-ui-only' }),
  action('reply', 'Reply', 'communication', 100, {
    primaryAction: 'Open or restore the unified compose dock within 150ms.',
    permission: 'email.write',
    busyKey: 'compose:reply',
    stories: ['US-05'],
  }, { visibility: 'always' }),
  action('reply_all', 'Reply all', 'communication', 110, {
    primaryAction: 'Open compose to all recipients with deduped sender/self.',
    permission: 'email.write',
    busyKey: 'compose:reply',
    stories: ['US-06'],
  }),
  action('forward', 'Forward', 'communication', 120, {
    primaryAction: 'Open compose preserving allowed attachments.',
    permission: 'email.write',
    busyKey: 'compose:forward',
    stories: ['US-07'],
  }),
  action('done', 'Done', 'communication', 130, {
    primaryAction: 'Optimistically archive plus complete linked card, then advance.',
    permission: 'email.write',
    busyKey: 'email:done',
    stories: ['US-13'],
  }, {
    optimistic: true,
    optimisticReducer: 'remove-row-and-advance',
    undoable: true,
    inverse: {
      kind: 'undo-toast',
      undoWindowMs: UNDO_TOAST_MS,
      description: 'Undo restores the inbox row and the linked card state.',
    },
    contextActions: ['done_no_advance', 'done_open_next', 'open_linked_card'],
    inputs: CONTEXTUAL,
    errors: errorMapping({
      conflict:
        'The linked card changed elsewhere. Both email and card were restored.',
    }),
  }),
  action('more', 'More actions', 'communication', 140, {
    primaryAction: 'Open the grouped long-tail menu in under 100ms from this registry.',
    permission: 'none',
    busyKey: 'communication:more',
    stories: ['US-14', 'US-15'],
  }, { visibility: 'always', optimisticReducer: 'local-ui-only' }),
  action('find', 'Find in thread', 'communication', 150, {
    primaryAction: 'Open in-thread find and focus the query field.',
    permission: 'none',
    busyKey: 'communication:find',
    stories: ['US-04'],
  }, { visibility: 'always', optimisticReducer: 'local-ui-only' }),
  action('expand', 'Open full view', 'communication', 160, {
    primaryAction: 'Open the shared frame in full view preserving scroll, context, find, and draft.',
    permission: 'none',
    busyKey: 'communication:expand',
    stories: ['US-03'],
  }, {
    visibleWhen: (model) => model.surface === 'preview',
    optimisticReducer: 'local-ui-only',
  }),
  action('accept_decision', 'Accept suggestion', 'decision', 200, {
    primaryAction: 'Confirm the current highest-priority decision and advance the queue.',
    permission: 'email.write',
    busyKey: 'decision:current',
    stories: ['US-09', 'US-11', 'US-12'],
  }, {
    inputs: CONTEXTUAL,
    visibleWhen: (model) => model.currentDecision != null,
    optimistic: true,
    optimisticReducer: 'resolve-decision',
    undoable: true,
    errors: errorMapping({
      conflict: 'Changed since suggested. Review the updated state before accepting.',
    }),
  }),
  action('reject_decision', 'Reject suggestion', 'decision', 210, {
    primaryAction: 'Reject the current decision, apply its reject effect, and advance.',
    permission: 'email.write',
    busyKey: 'decision:current',
    stories: ['US-10', 'US-11'],
  }, {
    inputs: CONTEXTUAL,
    visibleWhen: (model) => model.currentDecision != null,
    optimistic: true,
    optimisticReducer: 'resolve-decision',
    undoable: true,
  }),
  action('open_card', 'Open card', 'context', 300, {
    primaryAction: 'Open the primary placement card peek without leaving the email.',
    permission: 'email.read',
    busyKey: 'context:open-card',
    stories: ['US-19'],
  }, {
    inputs: CONTEXTUAL,
    visibleWhen: (model) => model.primaryPlacement != null,
    contextActions: ['why_this_placement', 'open_board', 'change_lane', 'move_to_board', 'remove_placement'],
    errors: errorMapping({
      notfound: 'This card was deleted or made private. Remove the stale link or relink.',
    }),
  }),
  action('open_board', 'Open board', 'context', 310, {
    primaryAction: 'Open the primary placement board.',
    permission: 'email.read',
    busyKey: 'context:open-board',
    stories: ['US-19', 'US-20'],
  }, {
    inputs: CONTEXTUAL,
    visibleWhen: (model) => model.primaryPlacement != null,
  }),
  action('manage_placements', 'Manage placements', 'context', 320, {
    primaryAction: 'Open the Placement Manager listing every active placement.',
    permission: 'kanban.write',
    busyKey: 'context:placements',
    stories: ['US-20', 'US-21'],
  }, {
    inputs: CONTEXTUAL,
    // Bare Filed-To-lane menu (plan "Filed To lane" → bare-lane list).
    contextActions: ['open_placement_manager', 'add_placement', 'link_existing_card', 'explain_filing', 'request_filing_rule'],
    optimistic: true,
    optimisticReducer: 'patch-placements',
    undoable: true,
    errors: errorMapping({
      conflict: 'This placement changed elsewhere or already exists. Opening the existing placement.',
    }),
  }),
  action('manage_company', 'Company actions', 'context', 330, {
    primaryAction: 'Open the company/sender context for the resolved identity.',
    permission: 'email.read',
    busyKey: 'context:company',
    stories: ['US-16', 'US-25'],
  }, {
    inputs: CONTEXTUAL,
    // Bare Company-lane menu (plan "Company lane" → bare-lane list).
    contextActions: ['open_company_context', 'change_relationship', 'filter_by_company', 'ask_ai_identity'],
    optimisticReducer: 'local-ui-only',
  }),
  action('manage_classification', 'Classification actions', 'context', 340, {
    primaryAction: 'Open the category picker at the current selection.',
    permission: 'email.write',
    busyKey: 'context:classification',
    stories: ['US-17', 'US-25'],
  }, {
    inputs: CONTEXTUAL,
    // Bare Classification-lane menu (plan "Classification lane" → bare-lane list).
    contextActions: ['ask_ai_classification', 'request_classification_rule', 'create_personal_label', 'create_team_label'],
    optimisticReducer: 'local-ui-only',
  }),
  action('manage_signals', 'Intelligence actions', 'context', 350, {
    primaryAction: 'Open the Signals Manager listing every structured signal and tag.',
    permission: 'email.read',
    busyKey: 'context:signals',
    stories: ['US-18', 'US-25'],
  }, {
    inputs: CONTEXTUAL,
    // Bare Intelligence-lane menu (plan "Intelligence lane" → bare-lane list).
    contextActions: ['manage_tags', 'add_tag', 'copy_all_signals', 'copy_ai_summary', 'ask_ai_signals'],
    optimisticReducer: 'local-ui-only',
  }),
  action('manage_owner', 'Manage owner', 'workflow', 400, {
    primaryAction: 'Open the roster picker and assign or clear the owner.',
    permission: 'workflow.write',
    busyKey: 'workflow:owner',
    stories: ['US-22'],
  }, {
    inputs: CONTEXTUAL,
    contextActions: ['change_owner', 'clear_owner', 'ping_owner', 'open_profile'],
    optimistic: true,
    optimisticReducer: 'patch-workflow-cell',
    undoable: true,
  }),
  action('manage_due', 'Manage due date', 'workflow', 410, {
    primaryAction: 'Pick Today, Tomorrow, This week, a date, or No date.',
    permission: 'workflow.write',
    busyKey: 'workflow:due',
    stories: ['US-22'],
  }, {
    inputs: CONTEXTUAL,
    contextActions: ['due_today', 'due_tomorrow', 'due_this_week', 'due_pick_date', 'due_clear', 'copy_date'],
    optimistic: true,
    optimisticReducer: 'patch-workflow-cell',
    undoable: true,
  }),
  action('manage_watchers', 'Manage watchers', 'workflow', 420, {
    primaryAction: 'Open the Watcher Manager to invite or remove watchers.',
    permission: 'workflow.write',
    busyKey: 'workflow:watchers',
    stories: ['US-23'],
  }, {
    inputs: CONTEXTUAL,
    contextActions: ['invite_watcher', 'remove_watcher', 'ping_watcher', 'copy_watcher_list'],
    optimistic: true,
    optimisticReducer: 'patch-workflow-cell',
    undoable: true,
  }),
  action('ping', 'Ping teammate', 'overflow', 500, {
    primaryAction: 'Open the estimators-first people picker with a note field.',
    permission: 'email.write',
    busyKey: 'overflow:ping',
    stories: ['US-08'],
  }, { visibility: 'overflow', overflowGroup: 'workflow' }),
  action('link_job', 'Link to job', 'overflow', 510, {
    primaryAction: 'Open the searchable job picker; existing link shows Open/Change/Remove first.',
    permission: 'email.write',
    busyKey: 'overflow:link-job',
    stories: ['US-16'],
  }, { visibility: 'overflow', overflowGroup: 'workflow' }),
  action('send_to_inbox', 'Send to Kanban Inbox', 'overflow', 520, {
    primaryAction: 'Create or reveal the Kanban inbox card and acknowledge immediately.',
    permission: 'kanban.write',
    busyKey: 'overflow:send-to-inbox',
    stories: ['US-21'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'workflow',
    optimistic: true,
    optimisticReducer: 'patch-placements',
    errors: errorMapping({
      notfound: 'No Kanban workspace exists yet. Set one up first.',
    }),
  }),
  action('schedule', 'Schedule meeting', 'overflow', 530, {
    primaryAction: 'Open the calendar composer seeded with sender, recipients, and email link.',
    permission: 'email.write',
    busyKey: 'overflow:schedule',
    stories: ['US-31'],
  }, { visibility: 'overflow', overflowGroup: 'workflow' }),
  action('who_can_see', 'Who can see', 'overflow', 540, {
    primaryAction: 'Show current people/organisation visibility with the reason.',
    permission: 'email.read',
    busyKey: 'overflow:visibility',
    stories: ['US-33'],
  }, { visibility: 'overflow', overflowGroup: 'access' }),
  action('mark_read', 'Mark read or unread', 'overflow', 550, {
    primaryAction: 'Toggle read state immediately in local state.',
    permission: 'email.write',
    busyKey: 'email:read-state',
    stories: ['US-26'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'state',
    optimistic: true,
    optimisticReducer: 'patch-email-flags',
    undoable: true,
    inverse: { kind: 'toggle', description: 'Invoking again restores the prior state.' },
  }),
  action('star', 'Star or unstar', 'overflow', 560, {
    primaryAction: 'Toggle the star immediately in local state.',
    permission: 'email.write',
    busyKey: 'email:star',
    stories: ['US-26'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'state',
    optimistic: true,
    optimisticReducer: 'patch-email-flags',
    undoable: true,
    inverse: { kind: 'toggle', description: 'Invoking again restores the prior state.' },
  }),
  action('snooze', 'Snooze', 'overflow', 570, {
    primaryAction: 'Hide the email until an exact return time stated with timezone.',
    permission: 'email.write',
    busyKey: 'email:snooze',
    stories: ['US-26', 'US-32'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'state',
    optimistic: true,
    optimisticReducer: 'patch-email-flags',
    undoable: true,
    inverse: { kind: 'inverse-action', description: 'Unsnooze restores the row immediately.' },
  }),
  action('archive', 'Archive', 'overflow', 580, {
    primaryAction: 'Remove the mail row only; linked work is untouched (Done completes work).',
    permission: 'email.write',
    busyKey: 'email:archive',
    stories: ['US-26'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'state',
    optimistic: true,
    optimisticReducer: 'remove-row-and-advance',
    undoable: true,
  }),
  action('junk', 'Mark junk', 'overflow', 590, {
    primaryAction: 'Junk this email immediately; sender/domain rules need a separate confirmation.',
    permission: 'email.write',
    busyKey: 'email:junk',
    stories: ['US-14'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'risk',
    inputs: CONTEXTUAL,
    contextActions: ['junk_this_email', 'auto_trash_sender', 'auto_trash_domain'],
    optimistic: true,
    optimisticReducer: 'remove-row-and-advance',
    undoable: true,
    dangerous: true,
  }),
  action('delete', 'Move to bin', 'overflow', 600, {
    primaryAction: 'Optimistically remove and advance; failure re-inserts at the original position.',
    permission: 'email.delete',
    busyKey: 'email:delete',
    stories: ['US-15'],
  }, {
    visibility: 'overflow',
    overflowGroup: 'risk',
    optimistic: true,
    optimisticReducer: 'remove-row-and-advance',
    undoable: true,
    dangerous: true,
  }),
  action('zoom_out', 'Zoom out', 'overflow', 610, {
    primaryAction: 'Reduce body zoom by 10% within 60-250%.',
    permission: 'none',
    busyKey: 'view:zoom',
    stories: ['US-34'],
  }, { visibility: 'overflow', overflowGroup: 'view', visibleWhen: (model) => model.surface === 'full', optimisticReducer: 'local-ui-only' }),
  action('zoom_reset', 'Reset zoom', 'overflow', 620, {
    primaryAction: 'Restore body zoom to 100%.',
    permission: 'none',
    busyKey: 'view:zoom',
    stories: ['US-34'],
  }, { visibility: 'overflow', overflowGroup: 'view', visibleWhen: (model) => model.surface === 'full', optimisticReducer: 'local-ui-only' }),
  action('zoom_in', 'Zoom in', 'overflow', 630, {
    primaryAction: 'Increase body zoom by 10% within 60-250%.',
    permission: 'none',
    busyKey: 'view:zoom',
    stories: ['US-34'],
  }, { visibility: 'overflow', overflowGroup: 'view', visibleWhen: (model) => model.surface === 'full', optimisticReducer: 'local-ui-only' }),
  action('copy_sender_address', 'Copy email address', 'overflow', 640, {
    primaryAction: 'Copy the sender address to the clipboard with a confirming toast.',
    permission: 'email.read',
    busyKey: 'communication:copy',
    stories: ['US-14'],
  }, { visibility: 'overflow', overflowGroup: 'communication', optimisticReducer: 'local-ui-only' }),
  action('copy_email_link', 'Copy link to email', 'overflow', 650, {
    primaryAction: 'Copy an org-scoped deep link that opens this email.',
    permission: 'email.read',
    busyKey: 'communication:copy',
    stories: ['US-14'],
  }, { visibility: 'overflow', overflowGroup: 'communication', optimisticReducer: 'local-ui-only' }),
]

/**
 * The stable core command order (plan D269-14). Rendering code must derive
 * command-bar order from this list and never reorder it by usage.
 */
export const CORE_COMMAND_ORDER: readonly ReaderActionId[] = [
  'reply',
  'reply_all',
  'forward',
  'done',
  'more',
  'find',
  'expand',
]

const ACTION_BY_ID = new Map(READER_ACTIONS.map((descriptor) => [descriptor.id, descriptor]))

export function getReaderAction(id: ReaderActionId): ReaderActionDescriptor {
  const descriptor = ACTION_BY_ID.get(id)
  if (!descriptor) throw new Error(`Reader action "${id}" is not registered`)
  return descriptor
}

// More-menu grouping data lives in ./overflowGroups.ts (split for the
// 500-line ceiling when the context-lane owner actions were added, #269 P4).
